export default function useModulesValidation() {
  const { getFullObjectFromApi } = useFeatureFlagsNew();
  const { isKycIntegrationEnabled } = useKyc();
  const { temporaryPassword } = storeToRefs(useUserDataStore());

  const possibleModulesOrdered: Containment.PendencyName[] = [
    'docs',
    'terms',
    'email',
    'phone',
    'address',
    'kyc',
    'gps',
    'password',
    'user_limits',
    'bank_account',
    'contact',
  ];

  const validationModulesFromApi = computed<ValidationModules>(() => {
    const modulesFromApi =
      getFullObjectFromApi<ValidationModules>('validation_modules');

    return modulesFromApi || {};
  });

  function isModuleActive(name: keyof ValidationModules): boolean {
    const validationModules = validationModulesFromApi.value;

    return !!validationModules[name]?.active;
  }

  function getCustomModules(
    name: keyof ValidationModules,
  ): Containment.Validation[] {
    if (name === 'global') {
      const { isContainmentDocsEnabled, isContainmentTermsEnabled } =
        useFeatureFlagsJson();

      const customModules: Containment.Validation[] = [];

      if (isContainmentDocsEnabled.value) {
        customModules.push({
          name: 'docs',
          frequent: false,
        });
      }

      if (isContainmentTermsEnabled.value) {
        customModules.push({
          name: 'terms',
          frequent: false,
        });
      }

      return customModules;
    }

    return [];
  }

  function getValidationModule(
    name: keyof ValidationModules,
  ): Containment.Validation[] {
    const validationModules = validationModulesFromApi.value;
    const customModules = getCustomModules(name);

    if (!isModuleActive(name)) {
      return [...customModules];
    }

    const modules = validationModules[name]?.modules || [];

    const sortedModules = possibleModulesOrdered.filter((moduleName) => {
      return modules.includes(moduleName);
    });

    if (!sortedModules) {
      return [...customModules];
    }

    const filteredModules = sortedModules.filter((moduleName) => {
      if (!isKycIntegrationEnabled.value && moduleName === 'kyc') {
        return false;
      }

      // Temp: do not activate the `email` module when it is `frequent`
      if (moduleName === 'email' && validationModules[name].frequent) {
        return false;
      }

      // Temp: do not activate the `phone` module when it is `frequent`
      if (moduleName === 'phone' && validationModules[name].frequent) {
        return false;
      }

      // Temp: do not activate the `gps` module
      // ATENCAO: reativar este modulo quebra o app mobile (WebView).
      // O app em rn-app-7k nao declara as permissoes de localizacao no
      // Android (ACCESS_FINE_LOCATION / ACCESS_COARSE_LOCATION em app.json)
      // nem habilita `geolocationEnabled` + `onGeolocationPermissionsShowPrompt`
      // no componente <WebView> (components/ViewByStatus/ViewByStatus.tsx).
      // Sem isso, navigator.geolocation.getCurrentPosition falha
      // silenciosamente dentro da WebView Android, o containment retorna
      // false e o usuario fica preso. Antes de remover este filtro,
      // ajustar o app mobile para conceder a permissao nativa e repassar
      // a geolocalizacao para o WebView.
      if (moduleName === 'gps') {
        return false;
      }

      return true;
    });

    if (!filteredModules.length) {
      return [...customModules];
    }

    const mapedModules = filteredModules.map((moduleName) => {
      const isFrequentContext = !!validationModules[name]?.frequent;

      // Keep `contact` visible in frequent contexts, but signal as validated if user already verified
      if (moduleName === 'contact' && isFrequentContext) {
        return { name: moduleName, frequent: false };
      }

      return {
        name: moduleName,
        frequent: isFrequentContext,
      };
    });

    return [...customModules, ...mapedModules];
  }

  function isValidationModuleEnabled(name: keyof ValidationModules): boolean {
    if (!name) {
      return false;
    }

    return !!getValidationModule(name).length;
  }

  function isTriggerModuleOnCurrentDevice(
    name: keyof ValidationModules,
  ): boolean {
    const moduleName = validationModulesFromApi.value[name];

    // Currently, only the 'register' module has the device
    if (name !== 'register' || !moduleName) {
      return true;
    }

    const { isLayoutMobileOrTablet, isDesktop } = useLayout();

    // Currently, only the 'register' module has the device
    const registerModule = moduleName as ValidationModules['register'];

    if (registerModule.device === 'mobile') {
      return isLayoutMobileOrTablet.value;
    }

    if (registerModule.device === 'desktop') {
      return isDesktop.value;
    }

    return true;
  }

  function getMinValueToTriggerModule(name: keyof ValidationModules): number {
    const moduleName = validationModulesFromApi.value[name];

    if (!moduleName) {
      return 1;
    }

    const minValue = (moduleName as any)?.min_value;

    if (minValue === undefined || minValue === null) {
      return 1;
    }

    return Number(minValue);
  }

  function isModuleNeededOnFirstWithdraw(name: Containment.PendencyName) {
    return getValidationModule('first_withdraw')
      .map((module) => module.name)
      .includes(name);
  }

  const isContainmentMiddlewareEnabled = computed<boolean>(() => {
    const { isKycForcedRequest } = useUserData();

    return (
      !!isValidationModuleEnabled('global') ||
      !!isValidationModuleEnabled('casino') ||
      !!isValidationModuleEnabled('sports') ||
      isKycForcedRequest.value
    );
  });

  const isContainmentCasinoListEnabled = computed<boolean>(() => {
    if (!isValidationModuleEnabled('casino')) {
      return false;
    }

    return validationModulesFromApi.value.casino?.active_on_lists || false;
  });

  const temporaryPasswordManager = {
    set(password: string) {
      temporaryPassword.value = btoa(password);
    },
    get() {
      return atob(temporaryPassword.value);
    },
    clear() {
      temporaryPassword.value = '';
    },
  };

  return {
    getValidationModule,
    isValidationModuleEnabled,
    isTriggerModuleOnCurrentDevice,
    getMinValueToTriggerModule,
    isModuleNeededOnFirstWithdraw,
    isContainmentMiddlewareEnabled,
    isContainmentCasinoListEnabled,
    temporaryPassword: temporaryPasswordManager,
  };
}
